<template>
	<div class="chat-item" :class="{ active: active, 'drag-over': isDragOver }" @click="handleClick" @dblclick="handleDoubleClick" @contextmenu.prevent="showRightMenu($event)" v-bind="$attrs">
		<!-- 拖拽提示 -->
		<div v-if="isDragOver" class="drag-hint">
			<span>松开发送</span>
		</div>

		<div class="chat-left">
			<head-image
				:url="chat.headImage"
				:name="chat.showName"
				:size="42"
				:id="headImageId"
				:isShowUserInfo="chat.type !== 'ai'">
			</head-image>

			<!-- 智能体状态指示器（二级展开状态） -->
			<div v-if="isAgentChat && agentStatus && agentStatus.isPulsing"
				class="status-indicator"
				:class="{ 'pulsing': agentStatus.isPulsing }"
				:style="{ backgroundColor: agentStatus.statusDotColor }">
			</div>

			<div v-show="chat.unreadCount > 0" class="unread-text">{{ chat.unreadCount }}</div>
		</div>
		<div class="chat-right">
			<div class="chat-name">
				<div class="chat-name-text">
					<div>{{ chat.showName }}</div>
					<el-tag v-if="chat.type == 'GROUP'" size="mini" effect="dark">群</el-tag>
					<!-- <el-tag v-if="chat.isTerminalInbox" size="mini" type="success" effect="dark">终端</el-tag> -->
				</div>
				<div class="chat-time-text" v-show="!isTerminalChat">{{ showTime }}</div>
			</div>
			<div class="chat-content" v-show="!isTerminalChat">
				<div class="chat-at-text">{{ atText }}</div>
				<div class="chat-send-name" v-show="isShowSendName">{{ chat.sendNickName + ':&nbsp;' }}</div>

				<!-- 智能体聚合条目 -->
				<template v-if="chat.type === 'AGENT_AGGREGATE'">
					<!-- 有活跃智能体：显示轮播 -->
					<agent-status-carousel
						v-if="chat.activeAgents && chat.activeAgents.length > 0"
						:active-agents="chat.activeAgents"
						:interval="3000"
						class="chat-content-text">
					</agent-status-carousel>

					<!-- 没有活跃智能体：显示静态文本（共X个会话） -->
					<div v-else class="chat-content-text" v-html="$emo.transform(chat.lastContent,'emoji-small')"></div>
				</template>

				<!-- 智能体会话：根据状态切换显示 -->
				<template v-else-if="isAgentChat">
					<!-- 正在执行：显示状态文本 -->
					<div v-if="agentStatus && agentStatus.isPulsing"
						class="chat-content-text agent-status-text"
						:style="{ color: agentStatus.statusColor }">
						{{ agentStatus.statusText }}
					</div>
					<!-- 空闲/离线：显示最后一条消息 -->
					<div v-else class="chat-content-text" v-html="$emo.transform(chat.lastMessage || chat.lastContent || '暂无消息', 'emoji-small')"></div>
				</template>

				<!-- 普通条目：显示 lastContent -->
				<div v-else class="chat-content-text" v-html="$emo.transform(chat.lastContent,'emoji-small')"></div>
			</div>
		</div>
		<right-menu v-show="rightMenu.show" :pos="rightMenu.pos" :items="rightMenu.items"
			@close="rightMenu.show = false" @select="onSelectMenu"></right-menu>
	</div>

</template>

<script>
import HeadImage from '../common/HeadImage.vue';
import RightMenu from '../common/RightMenu.vue';
import AgentStatusCarousel from './AgentStatusCarousel.vue';
import { log } from "@/utils/logManager.js";

export default {
	name: "chatItem",
	inheritAttrs: false, // 手动处理属性绑定，允许 v-bind="$attrs" 工作
	components: {
		HeadImage,
		RightMenu,
		AgentStatusCarousel
	},
	data() {
		return {
			rightMenu: {
				show: false,
				pos: {
					x: 0,
					y: 0
				},
				items: []
			},
			// 拖拽状态
			isDragOver: false
		}
	},
	props: {
		chat: {
			type: Object
		},
		active: {
			type: Boolean
		},
		index: {
			type: Number
		},
		// 新增：是否禁用双击模式（改为单击即激活）
		disableDoubleClick: {
			type: Boolean,
			default: false
		},
		// 新增：是否禁用右键菜单
		disableContextMenu: {
			type: Boolean,
			default: false
		},
		// 新增：父级会话（用于二级面板的智能体会话）
		parentChat: {
			type: Object,
			default: null
		}
	},
	methods: {
		showRightMenu(e) {
			// 如果禁用了右键菜单，直接返回
			if (this.disableContextMenu) {
				return;
			}

			// 动态设置右键菜单项
			this.rightMenu.items = this.getMenuItems();

			// 临时方案：减去50px的偏移量
			this.rightMenu.pos = {
				x: e.clientX - 50,
				y: e.clientY - 50
			};
			this.rightMenu.show = "true";
		},
		getMenuItems() {
			const items = [];

			// 终端会话不显示置顶选项（因为它们已经置顶）
			if (!this.chat.isTerminalInbox) {
				items.push({
					key: 'TOP',
					name: '置顶',
					icon: 'el-icon-top'
				});
			}

			// 🔥 智能体会话显示"删除"，其他会话显示"不显示"
			if (!this.chat.isTerminalInbox) {
				if (this.chat.type === 'AGENT') {
					items.push({
						key: 'DELETE_AGENT',
						name: '删除',
						icon: 'el-icon-delete'
					});
				} else {
					items.push({
						key: 'DELETE',
						name: '不显示',
						icon: 'el-icon-delete'
					});
				}
			}

			return items;
		},
		onSelectMenu(item) {
			// 🔥 如果是智能体会话的删除操作，调用删除逻辑
			if (item.key === 'DELETE_AGENT') {
				this.deleteAgentChat();
			} else {
				// 其他操作，正常触发事件
				this.$emit(item.key.toLowerCase(), this.index);
			}
		},
		// 🔥 新增：删除智能体会话
		async deleteAgentChat() {
			try {
				// 确认删除
				await this.$confirm('确定要删除这个智能体会话吗？', '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				});

				// 调用删除接口
				await this.$http({
					url: `/message/agent/chat/${this.chat.targetId}`,
					method: 'DELETE'
				});

				// 🔥 从 Vuex 中移除智能体会话
				this.$store.commit('removeAgentChat', this.chat.targetId);

				// 🔥 从本地存储中删除元数据
				const localForage = (await import('localforage')).default;
				await localForage.removeItem(`${this.chat.targetId}_meta`);

				this.$message.success('删除成功');
			} catch (error) {
				if (error !== 'cancel') {
					console.error('[ChatItem] 删除智能体会话失败:', error);
					this.$message.error('删除失败');
				}
			}
		},
		handleClick() {
			// 使用统一日志管理器，设置为debug级别（正常情况下不显示）
			log.debug('ChatItem', '被点击了，索引:', this.index, '会话:', this.chat.showName);

			// 如果禁用双击模式，单击就激活
			if (this.disableDoubleClick) {
				log.debug('ChatItem', '单击模式：立即激活会话');
				this.$emit('active', this.index);
			}
			// 否则等待双击
		},
		handleDoubleClick() {
			// 双击激活（仅在非禁用双击模式下）
			if (!this.disableDoubleClick) {
				log.debug('ChatItem', '双击模式：激活会话');
				this.$emit('active', this.index);
			}
		},

		// 🆕 处理来自 Chat.vue 的 workspace-drag-over 事件
		handleWorkspaceDragOver(chatIndex) {
			// 检查是否是当前 ChatItem
			if (String(chatIndex) === String(this.index)) {
				this.isDragOver = true;
			} else {
				// 不是当前 item，清除状态
				this.isDragOver = false;
			}
		},

		// 🆕 处理来自 Chat.vue 的 workspace-drag-clear 事件
		handleWorkspaceDragClear() {
			this.isDragOver = false;
		}
	},
	mounted() {
		// 🆕 监听来自 Chat.vue 的拖拽事件（用于跨 iframe 拖拽）
		this.$eventBus.$on('workspace-drag-over', this.handleWorkspaceDragOver);
		this.$eventBus.$on('workspace-drag-clear', this.handleWorkspaceDragClear);

		// 🔥 同时也监听 $root 事件（备用方案）
		this.$root.$on('workspace-drag-over', this.handleWorkspaceDragOver);
		this.$root.$on('workspace-drag-clear', this.handleWorkspaceDragClear);
	},
	beforeDestroy() {
		// 🆕 移除来自 Chat.vue 的拖拽事件监听
		this.$eventBus.$off('workspace-drag-over', this.handleWorkspaceDragOver);
		this.$eventBus.$off('workspace-drag-clear', this.handleWorkspaceDragClear);

		// 🔥 也移除 $root 事件监听
		this.$root.$off('workspace-drag-over', this.handleWorkspaceDragOver);
		this.$root.$off('workspace-drag-clear', this.handleWorkspaceDragClear);
	},
	computed: {
		// 🔥 新增：计算实际的目标会话（用于文件发送）
		actualTargetChat() {
			// 1. 如果有 parentChat，直接使用（二级面板传递的父级会话）
			if (this.parentChat) {
				return this.parentChat;
			}

			// 2. 如果当前是智能体会话，尝试从 targetId 解析父级会话
			if (this.chat.type === 'AGENT' || (this.chat.targetId && this.chat.targetId.includes('_agent_'))) {
				const targetId = this.chat.targetId;
				if (targetId && targetId.includes('_agent_')) {
					// targetId 格式: GROUP_123_agent_agent_xxx
					const parts = targetId.split('_agent_');
					const parentInfo = parts[0]; // 例如: "GROUP_123"
					const [parentType, parentTargetId] = parentInfo.split('_');

					// 从 Vuex store 中找到父级会话
					const parentChat = this.$store.state.chatStore.chats.find(c =>
						c.type === parentType && c.targetId?.toString() === parentTargetId
					);

					if (parentChat) {
						return parentChat;
					}
				}
			}

			// 3. 默认使用当前会话
			return this.chat;
		},

		// 🔧 新增：计算HeadImage组件需要的数值ID
		headImageId() {
			if (this.chat.type !== 'PRIVATE') {
				return 0; // 非私聊会话返回0，不显示用户信息
			}

			// 🔧 处理终端收件箱会话的特殊targetId格式
			if (this.chat.isTerminalInbox && typeof this.chat.targetId === 'string') {
				// 从"1_2"格式中提取用户ID（第一部分）
				const parts = this.chat.targetId.split('_');
				const userId = parseInt(parts[0]);
				return isNaN(userId) ? 0 : userId;
			}

			// 普通私聊会话
			const targetId = parseInt(this.chat.targetId);
			return isNaN(targetId) ? 0 : targetId;
		},
		isShowSendName() {
			if (!this.chat.sendNickName) {
				return false;
			}
			let size = this.chat.messages.length;
			if (size == 0) {
				return false;
			}
			// 只有群聊的普通消息需要显示名称
			let lastMsg = this.chat.messages[size - 1];
			return this.$msgType.isNormal(lastMsg.type)
		},
		showTime() {
			return this.$date.toTimeText(this.chat.lastSendTime, true)
		},
		atText() {
			if (this.chat.atMe) {
				return "[有人@我]"
			} else if (this.chat.atAll) {
				return "[@全体成员]"
			}
			return "";
		},

		// 判断是否为智能体会话
		isAgentChat() {
			return this.chat.type === 'AGENT';
		},

		// 判断是否为终端会话（小龙虾端、云小龙虾端等）
		isTerminalChat() {
			return this.chat.isTerminalChat || this.chat.isTerminalInbox;
		},

		// 获取智能体会话状态
		agentStatus() {
			if (!this.isAgentChat) return null;

			// 从 Vuex store 获取状态
			return this.$store.getters.getAgentStatus(this.chat.targetId);
		}
	}
}
</script>

<style lang="scss">
.chat-item {
	height: 50px;
	display: flex;
	position: relative;
	padding: 5px 10px;
	align-items: center;
	background-color: var(--im-background);
	white-space: nowrap;
	cursor: pointer;

	&:hover {
		background-color: var(--im-background-active);
	}

	// 拖拽悬停时的背景色
	&.drag-over {
		background-color: #e6f7ff;
		border-left: 3px solid #1890ff;
	}

	// 拖拽提示
	.drag-hint {
		position: absolute;
		right: 0;
		top: 0;
		bottom: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		background: linear-gradient(90deg, #1890ff, #40a9ff);
		color: white;
		padding: 0 16px;
		border-radius: 0;
		font-size: 13px;
		font-weight: 500;
		box-shadow: -2px 0 8px rgba(24, 144, 255, 0.3);
		z-index: 10;
		animation: slideInFromRight 0.3s ease-out;
		white-space: nowrap;

		span {
			display: inline-block;
		}
	}

	@keyframes slideInFromRight {
		from {
			transform: translateX(100%);
			opacity: 0;
		}
		to {
			transform: translateX(0);
			opacity: 1;
		}
	}

	.chat-left {
		position: relative;
		display: flex;
		justify-content: center;
		align-items: center;

		.unread-text {
			position: absolute;
			background-color: #f56c6c;
			right: -4px;
			top: -8px;
			color: white;
			border-radius: 30px;
			padding: 1px 5px;
			font-size: 10px;
			text-align: center;
			white-space: nowrap;
			border: 1px solid #f1e5e5;
		}

		// 智能体状态指示器
		.status-indicator {
			position: absolute;
			width: 12px;
			height: 12px;
			border-radius: 50%;
			bottom: 0;
			right: 0;
			border: 2px solid white;
			z-index: 10;

			&.pulsing {
				animation: pulse 2s infinite;
			}
		}
	}

	@keyframes pulse {
		0%, 100% {
			opacity: 1;
			transform: scale(1);
		}
		50% {
			opacity: 0.5;
			transform: scale(1.15);
		}
	}


	.chat-right {
		flex: 1;
		display: flex;
		flex-direction: column;
		padding-left: 10px;
		text-align: left;
		overflow: hidden;

		.chat-name {
			display: flex;
			line-height: 20px;
			height: 20px;

			.chat-name-text {
				flex: 1;
				display: flex;
				align-items: center;
				font-size: var(--im-font-size);
				white-space: nowrap;
				overflow: hidden;

				.el-tag {
					min-width: 22px;
					text-align: center;
					background-color: #2830d3;
					border-radius: 10px;
					border: 0;
					height: 16px;
					line-height: 16px;
					font-size: 10px;
					margin-left: 2px;
					opacity: 0.8;

				}
			}

			.chat-time-text {
				font-size: var(--im-font-size-smaller);
				text-align: right;
				color: var(--im-text-color-light);
				white-space: nowrap;
				overflow: hidden;
				padding-left: 10px;
			}
		}

		.chat-content {
			display: flex;
			line-height: 22px;

			.chat-at-text {
				color: #c70b0b;
				font-size: var(--im-font-size-smaller);
			}

			.chat-send-name {
				font-size: var(--im-font-size-small);
				color: var(--im-text-color-light);
			}


			.chat-content-text {
				flex: 1;
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
				font-size: var(--im-font-size-small);
				color: var(--im-text-color-light);

				// 智能体状态文本样式
				&.agent-status-text {
					font-weight: 500;
					font-style: italic;
					opacity: 0.9;
					// 颜色由内联样式动态设置 (agentStatus.statusColor)
				}
			}

		}
	}
}
</style>
